import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.hasAttribute('data-release-lazy-media')) this.#loadMediaInOrder(signal);
  }

  /**
   * Loads the product page gallery in priority order once the main image (preloaded in <head>) is in:
   * 1. The thumbnails, at high priority.
   * 2. Images 2 and 3, straight after the thumbnails are requested, at low priority.
   * 3. The lazy images (4 and later), once image 3 has finished. Skipped in data saver mode.
   * The thumbnails and images 2–3 are rendered with data-deferred-src/srcset so they can't start early.
   * On first page load an inline script in snippets/product-media-gallery-content.liquid releases them
   * from the main image's load event, well before this module runs; the release here then finds nothing
   * left and only covers the cases that script doesn't (a variant switch, a main image that never settles).
   * Each wait gives up after 4 seconds so a slow image doesn't hold back the rest. Zoom dialog copies
   * stay lazy. Runs again for the new gallery after a variant switch.
   *
   * @param {AbortSignal} signal
   */
  async #loadMediaInOrder(signal) {
    // Grid presentations render a grid (desktop) and a slideshow (mobile); use the one on screen.
    // Keep in step with the inline release script in snippets/product-media-gallery-content.liquid.
    const grid = this.querySelector('.media-gallery__grid');
    const gridVisible = grid instanceof HTMLElement && (grid.checkVisibility?.() ?? grid.offsetParent !== null);
    const slides = [...(gridVisible ? grid.children : (this.refs.slideshow?.querySelectorAll('slideshow-slide') ?? []))];
    const slideImages = slides.map((slide) => slide.querySelector('img.product-media__image'));

    const mainImage = slideImages[0];
    if (mainImage instanceof HTMLImageElement && !mainImage.complete) await imageSettled(mainImage, signal);
    if (signal.aborted) return;

    this.#releaseDeferredMedia(slides);
    this.#watchDeferredMedia(signal);

    if (/** @type {any} */ (navigator).connection?.saveData) return;

    const lazyImages = slides.slice(3).flatMap((slide) => [...slide.querySelectorAll('img[loading="lazy"]')]);
    if (!lazyImages.length) return;

    // The inline script may have released image 3 long enough ago that it has already loaded.
    const thirdImage = slideImages[2];
    if (thirdImage instanceof HTMLImageElement && !thirdImage.complete) await imageSettled(thirdImage, signal);
    if (signal.aborted) return;

    for (const image of lazyImages) {
      if (!(image instanceof HTMLImageElement) || image.loading !== 'lazy') continue;
      image.fetchPriority = 'low';
      image.loading = 'eager';
    }
  }

  /**
   * Gives the deferred images their src: thumbnails first, then images 2–3. Copies outside the visible
   * slides (the hidden grid or slideshow) are made lazy so they don't download while hidden.
   *
   * @param {Element[]} [visibleSlides] - The slides on screen; all slides when omitted.
   */
  #releaseDeferredMedia(visibleSlides) {
    const deferred = [...this.querySelectorAll('img[data-deferred-src]:not([src])')];
    const thumbnails = deferred.filter((image) => image.closest('.slideshow-controls__thumbnail'));

    for (const image of thumbnails) releaseImage(image, 'eager', 'high');

    for (const image of deferred) {
      if (thumbnails.includes(image)) continue;

      const visible = !visibleSlides || visibleSlides.some((slide) => slide.contains(image));
      releaseImage(image, visible ? 'eager' : 'lazy', 'low');
    }
  }

  /**
   * A morph (e.g. the quick add modal opening another product) updates the gallery in place without
   * reconnecting it, and can strip the src set by #releaseDeferredMedia. Release those again straight away.
   *
   * @param {AbortSignal} signal
   */
  #watchDeferredMedia(signal) {
    const observer = new MutationObserver(() => {
      if (this.querySelector('img[data-deferred-src]:not([src])')) this.#releaseDeferredMedia();
    });
    observer.observe(this, { subtree: true, childList: true, attributeFilter: ['src'] });
    signal.addEventListener('abort', () => observer.disconnect(), { once: true });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

/**
 * Resolves once the image loads or fails, after 4 seconds, or on abort. Only listens for the events,
 * so call it on an image whose src was just set, or one that isn't `complete` yet.
 *
 * @param {HTMLImageElement} image
 * @param {AbortSignal} signal
 * @returns {Promise<void>}
 */
function imageSettled(image, signal) {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(done, 4000);
    image.addEventListener('load', done, { once: true, signal });
    image.addEventListener('error', done, { once: true, signal });
    signal.addEventListener('abort', done, { once: true });
  });
}

/**
 * Moves an image's data-deferred-src/srcset into src/srcset, which starts the download.
 *
 * @param {Element} image
 * @param {'eager' | 'lazy'} loading
 * @param {'high' | 'low' | 'auto'} fetchPriority
 */
function releaseImage(image, loading, fetchPriority) {
  if (!(image instanceof HTMLImageElement)) return;

  const { deferredSrc, deferredSrcset } = image.dataset;
  image.loading = loading;
  image.fetchPriority = fetchPriority;
  if (deferredSrcset) image.srcset = deferredSrcset;
  if (deferredSrc) image.src = deferredSrc;
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
